import {
    ActionIcon,
    Badge,
    Box,
    Button,
    Group,
    Loader,
    Text,
    TextInput,
} from '@mantine/core'
import { Tag, X } from 'lucide-react'
import { useContext, useEffect, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Cart } from '@medusajs/medusa'

import { discountService } from '@/domain/Discount'
import { CartContext } from '@/modules/Cart/Cart.vm'
import formatAmount from '@/utils/format-amount'

export const PromoCodeInput = () => {
    const [code, setCode] = useState('')
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const [appliedCode, setAppliedCode] = useState<string | null>(null)
    const [discountAmount, setDiscountAmount] = useState<number | null>(null)

    const {
        cartActor: [actor],
        onUpdateCart,
    } = useContext(CartContext)

    const cart = actor.context.cart as (Cart & { discount_total?: number }) | undefined
    const cartId = cart?.id

    // Initialiser depuis le panier existant au montage
    useEffect(() => {
        if (cart?.discounts?.length) {
            const existing = cart.discounts[0]
            setAppliedCode(existing.code)
            setDiscountAmount(cart.discount_total ?? null)
        } else {
            // Le panier n'a plus de discount (ex: article ajouté) → reset
            setAppliedCode(null)
            setDiscountAmount(null)
        }
    }, [cartId, cart?.discounts?.length])

    const applyMutation = useMutation({
        mutationFn: async () => {
            if (!cartId) throw new Error('Panier introuvable')
            return await discountService.validateAndApply({
                code: code.trim().toUpperCase(),
                cart_id: cartId,
            })
        },
        onSuccess: (data) => {
            if (data.valid && data.cart) {
                setAppliedCode(code.trim().toUpperCase())
                setErrorMessage(null)
                setCode('')
                const updatedCart = data.cart as Cart & { discount_total?: number }
                setDiscountAmount(updatedCart.discount_total ?? null)
                onUpdateCart(data.cart as Cart)
            } else {
                setErrorMessage(data.message || 'Code invalide')
            }
        },
        onError: () => {
            setErrorMessage('Code invalide')
        },
    })

    const removeMutation = useMutation({
        mutationFn: async () => {
            if (!cartId || !appliedCode) throw new Error()
            return await discountService.remove({
                cart_id: cartId,
                code: appliedCode,
            })
        },
        onSuccess: (updatedCart) => {
            setAppliedCode(null)
            setDiscountAmount(null)
            setErrorMessage(null)
            onUpdateCart(updatedCart)
        },
        onError: () => {
            setErrorMessage('Impossible de supprimer le code promo.')
        },
    })

    const handleApply = () => {
        if (!code.trim()) return
        setErrorMessage(null)
        applyMutation.mutate()
    }

    return (
        <Box className="mt-4">
            {appliedCode ? (
                <Group spacing="xs" align="center">
                    <Tag className="w-4 h-4 text-green-600" />
                    <Badge
                        color="green"
                        variant="light"
                        rightSection={
                            <ActionIcon
                                size="xs"
                                color="green"
                                radius="xl"
                                variant="transparent"
                                onClick={() => removeMutation.mutate()}
                                loading={removeMutation.isPending}
                            >
                                <X className="w-3 h-3" />
                            </ActionIcon>
                        }
                    >
                        {appliedCode}
                    </Badge>
                    <Text size="xs" c="green.7">
                        {discountAmount
                            ? `- ${formatAmount(discountAmount)} de réduction`
                            : 'Code promo appliqué !'}
                    </Text>
                </Group>
            ) : (
                <Group spacing="xs" align="flex-end">
                    <TextInput
                        label="Code promo"
                        placeholder="Ex: SUMMER20"
                        value={code}
                        onChange={(e) =>
                            setCode(e.currentTarget.value.toUpperCase())
                        }
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') handleApply()
                        }}
                        size="xs"
                        className="flex-1"
                        icon={<Tag className="w-3 h-3" />}
                        error={errorMessage}
                        disabled={applyMutation.isPending}
                    />
                    <Button
                        size="xs"
                        color="dark"
                        variant="outline"
                        onClick={handleApply}
                        disabled={!code.trim() || applyMutation.isPending}
                        mb={errorMessage ? 22 : 0}
                    >
                        {applyMutation.isPending ? (
                            <Loader size="xs" />
                        ) : (
                            'Appliquer'
                        )}
                    </Button>
                </Group>
            )}
        </Box>
    )
}

export default PromoCodeInput
